import {
  Activity,
  Calendar,
  ClipboardList,
  CreditCardIcon,
  Heart,
  HelpCircle,
  Home,
  IdCard,
  Settings,
  Target,
} from 'lucide-react'

import { paths } from '@/config/paths'

import type { NavItem } from '@/types/nav-item'

/**
 * Member Navigation Configuration
 * Returns navigation items with translation function
 * @param t Translation function from useTranslation hook
 */
export const getMemberNav = (t: (key: string, defaultValue: string) => string): NavItem[] => [
  // Main
  {
    title: t('dashboard:nav.sidebar.dashboard', 'Dashboard'),
    url: paths.dashboard.root.getHref(),
    icon: Home,
    group: t('dashboard:nav.groups.main', 'Main'),
  },
  {
    title: t('dashboard:nav.sidebar.myMembership', 'My Membership'),
    url: paths.dashboard.member.myMembership.getHref(),
    icon: IdCard,
    group: t('dashboard:nav.groups.main', 'Main'),
  },

  // Classes
  {
    title: t('dashboard:nav.sidebar.bookClass', 'Book a Class'),
    url: paths.dashboard.member.bookClass.getHref(),
    icon: Calendar,
    group: t('dashboard:nav.groups.classes', 'Classes'),
  },
  {
    title: t('dashboard:nav.sidebar.myBookings', 'My Bookings'),
    url: paths.dashboard.member.myBookings.getHref(),
    icon: ClipboardList,
    group: t('dashboard:nav.groups.classes', 'Classes'),
  },

  // Health
  {
    title: t('dashboard:nav.sidebar.myHealth', 'My Health'),
    url: paths.dashboard.member.myHealthTracking.getHref(),
    icon: Heart,
    group: t('dashboard:nav.groups.health', 'Health'),
    items: [
      {
        title: t('dashboard:nav.sidebar.health.overview', 'Overview'),
        url: paths.dashboard.member.myHealthTracking.getHref(),
        icon: Heart,
      },
      {
        title: t('dashboard:nav.sidebar.health.vitals', 'Vitals'),
        url: paths.dashboard.healthTracking.vitals.root.getHref(),
        icon: Activity,
      },
      {
        title: t('dashboard:nav.sidebar.health.measurements', 'Measurements'),
        url: paths.dashboard.healthTracking.measurements.root.getHref(),
        icon: ClipboardList,
      },
      {
        title: t('dashboard:nav.sidebar.health.goals', 'Goals'),
        url: paths.dashboard.healthTracking.goals.root.getHref(),
        icon: Target,
      },
    ],
  },

  // Notes
  {
    title: t('dashboard:nav.sidebar.myNotes', 'My Notes'),
    url: paths.dashboard.member.myNotes.getHref(),
    icon: ClipboardList,
    group: t('dashboard:nav.groups.notes', 'Notes'),
  },

  // Finance
  {
    title: t('dashboard:nav.sidebar.myInvoices', 'My Invoices'),
    url: paths.dashboard.member.myPayments.getHref(),
    icon: CreditCardIcon,
    group: t('dashboard:nav.groups.finance', 'Finance'),
  },

  // Settings
  {
    title: t('dashboard:nav.sidebar.profileSettings', 'Profile Settings'),
    url: paths.dashboard.profile.root.getHref(),
    icon: Settings,
    group: t('dashboard:nav.groups.settings', 'Settings'),
  },
  {
    title: t('dashboard:nav.sidebar.support', 'Support'),
    url: paths.dashboard.support.root.getHref(),
    icon: HelpCircle,
    group: t('dashboard:nav.groups.settings', 'Settings'),
  },
]

// Legacy export for backward compatibility (will be removed in future versions)
// @deprecated Use getMemberNav(t) instead
export const memberNav: NavItem[] = [
  { title: 'Dashboard', url: '/', icon: Home, group: 'Main' },
  { title: 'My Membership', url: '/my-membership', icon: IdCard, group: 'Main' },
  { title: 'Book a Class', url: '/book-class', icon: Calendar, group: 'Classes' },
  { title: 'My Bookings', url: '/my-bookings', icon: ClipboardList, group: 'Classes' },
  {
    title: 'My Health',
    url: '/my-health-tracking',
    icon: Heart,
    group: 'Health',
    items: [
      { title: 'Overview', url: '/my-health-tracking', icon: Heart },
      { title: 'Vitals', url: '/my-health/vitals', icon: Activity },
      { title: 'Measurements', url: '/my-health/measurements', icon: ClipboardList },
      { title: 'Goals', url: '/my-health/goals', icon: Target },
    ],
  },
  { title: 'My Notes', url: '/my-notes', icon: ClipboardList, group: 'Notes' },
  { title: 'My Invoices', url: '/my-invoices', icon: CreditCardIcon, group: 'Finance' },
  { title: 'Profile Settings', url: '/profile', icon: Settings, group: 'Settings' },
  { title: 'Support', url: '/support', icon: HelpCircle, group: 'Settings' },
]
